import { useCallback, useMemo, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { BreadcrumbNav, Skeleton, StatusMessage, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ProviderHeader } from '../components';
import DisconnectImpactAlert from '../components/modals/DisconnectImpactAlert.jsx';
import { useProvider } from '../hooks/provider';
import { useIntegrationsPermissions } from '../hooks';
import ProviderTabPlaceholder from './provider/ProviderTabPlaceholder.jsx';
import OverviewTab from './provider/OverviewTab.jsx';
import CapabilitiesTab from './provider/CapabilitiesTab.jsx';
import InstancesTab from './provider/InstancesTab.jsx';
import UsageTab from './provider/UsageTab.jsx';
import ActivityTab from './provider/ActivityTab.jsx';
import AuditTab from './provider/AuditTab.jsx';
import WorkspaceAvailabilityTab from './provider/WorkspaceAvailabilityTab.jsx';
import { AlertTriangle } from 'lucide-react';

/** Real tab elements land here as each story ships; the rest fall back to the placeholder. */
const TAB_ELEMENTS = {
  overview: <OverviewTab />,
  capabilities: <CapabilitiesTab />,
  instances: <InstancesTab />,
  usage: <UsageTab />,
  activity: <ActivityTab />,
  audit: <AuditTab />,
  workspaces: <WorkspaceAvailabilityTab />,
};

/** Provider Detail tabs. Admin-only tabs are hidden for end users (CT-4185). */
const TABS = [
  { id: 'overview', path: 'overview', labelKey: 'integrations.provider.tabs.overview', ticket: 'CT-4179' },
  { id: 'capabilities', path: 'capabilities', labelKey: 'integrations.provider.tabs.capabilities', ticket: 'CT-4180' },
  { id: 'instances', path: 'instances', labelKey: 'integrations.provider.tabs.instances', adminOnly: true, ticket: 'CT-4181' },
  { id: 'usage', path: 'usage', labelKey: 'integrations.provider.tabs.usage', adminOnly: true, ticket: 'CT-4182' },
  { id: 'workspaces', path: 'workspaces', labelKey: 'integrations.provider.tabs.workspaces', adminOnly: true, connectedOnly: true, ticket: 'CT-4208' },
  { id: 'activity', path: 'activity', labelKey: 'integrations.provider.tabs.activity', adminOnly: true, ticket: 'CT-4183' },
  { id: 'audit', path: 'audit', labelKey: 'integrations.provider.tabs.audit', adminOnly: true, ticket: 'CT-4184' },
];

// Content inset is 1.5rem (`px-6`) — studio standard; no max-width gutter that
// doubles the side air against the shell. Sticky header pins inside this scroll.
const PAGE_WRAP = 'flex-1 overflow-y-auto bg-surface-base scrollbar-aimsos-auto-sm';
const PAGE_INNER = 'px-6';

const TAB_BASE = [
  'inline-flex items-center gap-[7px] rounded-t-md border-b-2 px-3.5 py-[11px]',
  'text-[13px] font-semibold tracking-[-0.005em] transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2B7FFF]/50 focus-visible:ring-inset',
].join(' ');
const TAB_IDLE = 'border-transparent text-content-secondary hover:bg-white/[0.03] hover:text-content';
const TAB_ACTIVE = 'border-[#2173FF] bg-transparent text-[#2173FF]';

/**
 * Provider Detail layout (CT-4186): owns the `useProvider(slug)` fetch, the
 * URL-routed tab bar, and the connected vs not-connected branching. Tabs read the
 * provider via `useOutletContext()`. The sticky header is CT-4178; the tab bodies
 * are CT-4179…4185 (placeholders until each lands).
 */
const ProviderDetailLayout = () => {
  const { t } = useTranslation('admin-studio');
  const { providerSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { provider, isLoading, error, refetch } = useProvider(providerSlug);
  const { canManageIntegrations } = useIntegrationsPermissions();
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const openDisconnect = useCallback(() => setDisconnectOpen(true), []);
  const closeDisconnect = useCallback(() => setDisconnectOpen(false), []);
  const notifySoon = useCallback(
    (key) => toast.info({ message: t(`integrations.provider.stubs.${key}`), canClose: true }),
    [toast, t],
  );
  const onConnectTab = useCallback(() => notifySoon('connect'), [notifySoon]);
  const outletContext = useMemo(
    () => ({ provider, isConnected: provider?.isConnected, onConnect: onConnectTab, notifySoon }),
    [provider, onConnectTab, notifySoon],
  );

  if (isLoading) {
    return (
      <main className={PAGE_WRAP}>
        <div className={`${PAGE_INNER} py-6`}>
          <Skeleton className="h-24 w-full" />
        </div>
      </main>
    );
  }
  if (error || !provider) {
    return (
      <main className={PAGE_WRAP}>
        <div className={`${PAGE_INNER} py-6`}>
          <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />
        </div>
      </main>
    );
  }

  // Workspace availability (CT-4208) only makes sense for a connected integration —
  // there's nothing to distribute for a marketplace item that isn't in the tenant yet.
  const visibleTabs = TABS.filter(
    (tab) =>
      (!tab.adminOnly || canManageIntegrations) && (!tab.connectedOnly || provider.isConnected),
  );

  // This layout mounts on a `:providerSlug/*` splat route nested several levels
  // deep. Splat-relative navigation (`navigate('overview')`) appends instead of
  // swapping the tab segment — spiraling the URL — so resolve every tab target
  // against the provider-root pathname. Derived from the live location, this is
  // correct in both standalone and Shell-embedded modes and needs no router flag.
  const segments = location.pathname.split('/');
  const providerIdx = segments.indexOf(providerSlug);
  const providerBase = segments.slice(0, providerIdx + 1).join('/');
  const integrationsBase = segments.slice(0, providerIdx).join('/') || '/';
  const activePath = segments[providerIdx + 1] ?? 'overview';
  const overviewPath = `${providerBase}/overview`;

  return (
    <main className={PAGE_WRAP}>
      <div className={PAGE_INNER}>
        <div className="pt-3.5">
          <BreadcrumbNav
            size="sm"
            items={[
              { label: t('integrations.home.title'), path: integrationsBase },
              { label: provider.name, path: providerBase },
            ]}
            onNavigate={(path) => navigate(path)}
          />
        </div>

        <div className="sticky top-0 z-20 -mx-6 mt-4 bg-surface-base/95 px-6 pt-6 backdrop-blur">
          <ProviderHeader
            provider={provider}
            onAskAI={() => notifySoon('askAI')}
            onRotate={() => notifySoon('rotate')}
            onDisconnect={openDisconnect}
            onConnect={() => notifySoon('connect')}
            onReview={() => notifySoon('dataStudio')}
            onOpenProvider={() => notifySoon('providerLink')}
          />
          {/* Prototype underline tabs, with the WAI-ARIA tab semantics from CT-4186. */}
          <nav
            role="tablist"
            aria-label={t('integrations.provider.tabs.ariaLabel')}
            className="-mx-6 mt-4 flex flex-wrap gap-1 border-b border-edge px-6"
          >
            {visibleTabs.map((tab) => {
              const active = activePath === tab.path;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => navigate(`${providerBase}/${tab.path}`)}
                  className={[TAB_BASE, active ? TAB_ACTIVE : TAB_IDLE].join(' ')}
                >
                  {t(tab.labelKey)}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Disconnect impact alert (CT-4187): hard gate before disconnecting. */}
        <DisconnectImpactAlert
          isOpen={disconnectOpen}
          slug={provider.slug}
          providerName={provider.name}
          onClose={closeDisconnect}
          onDisconnected={refetch}
        />

        <div className="flex flex-col gap-4 pb-6 pt-4">
          <Routes>
            <Route element={<Outlet context={outletContext} />}>
              <Route index element={<Navigate to={overviewPath} replace />} />
              {visibleTabs.map((tab) => (
                <Route
                  key={tab.id}
                  path={tab.path}
                  element={
                    TAB_ELEMENTS[tab.id] ?? (
                      <ProviderTabPlaceholder labelKey={tab.labelKey} ticket={tab.ticket} />
                    )
                  }
                />
              ))}
              <Route
                path="instances/:instanceId"
                element={
                  <ProviderTabPlaceholder
                    labelKey="integrations.provider.instanceDetail.title"
                    ticket="CT-3897"
                  />
                }
              />
              <Route path="*" element={<Navigate to={overviewPath} replace />} />
            </Route>
          </Routes>
        </div>
      </div>
    </main>
  );
};

export default ProviderDetailLayout;
